/**
 * NIP-51 Lists Service
 * 
 * Handles custom lists for bookmarks, favorites, and collections
 * Enforces strict consistency for moderation lists using MODERATION_RELAYS.
 */

import { Event, finalizeEvent } from 'nostr-tools'
import { getNostrPool } from '../lib/nostrPool'
import { DEFAULT_RELAYS, MODERATION_RELAYS } from '../config/constants'

// Verify this version is loaded
console.log('[ListService] Module loaded - Version with MODERATION_RELAYS and verification')

export type ListKind = 'bookmarks' | 'pins' | 'mutes' | 'custom' | 'moderation'

interface ListItem {
  type: 'event' | 'pubkey' | 'hashtag' | 'url'
  value: string
  relay?: string
  petname?: string
}

interface UserList {
  id: string
  name: string
  kind: number
  items: ListItem[]
  createdAt: number
  description?: string
}

/**
 * Get list kind number
 */
function getListKind(type: ListKind): number {
  switch (type) {
    case 'bookmarks': return 10003
    case 'pins': return 10001
    case 'mutes': return 10000
    case 'custom': return 30001
    case 'moderation': return 30000 // Categorized People/Event list, used for moderation_log
    default: return 10003
  }
}

/**
 * Helper: Choose relays based on list type
 * Moderation lists MUST use MODERATION_RELAYS to ensure global consistency.
 */
function getRelaysForList(type: ListKind): string[] {
  if (type === 'moderation') {
    return MODERATION_RELAYS
  }
  return DEFAULT_RELAYS
}

/**
 * Parse list items from event tags
 */
function parseListItems(tags: string[][]): ListItem[] {
  const items: ListItem[] = []

  for (const tag of tags) {
    if (tag[0] === 'e') {
      items.push({
        type: 'event',
        value: tag[1],
        relay: tag[2]
      })
    } else if (tag[0] === 'p') {
      items.push({
        type: 'pubkey',
        value: tag[1],
        relay: tag[2],
        petname: tag[3]
      })
    } else if (tag[0] === 't') {
      items.push({
        type: 'hashtag',
        value: tag[1]
      })
    } else if (tag[0] === 'r') {
      items.push({
        type: 'url',
        value: tag[1]
      })
    }
  }

  return items
}

/**
 * Convert list items to tags
 */
function itemsToTags(items: ListItem[]): string[][] {
  return items.map(item => {
    switch (item.type) {
      case 'event':
        return item.relay ? ['e', item.value, item.relay] : ['e', item.value]
      case 'pubkey':
        const pTag = ['p', item.value]
        if (item.relay) pTag.push(item.relay)
        if (item.petname) pTag.push(item.petname)
        return pTag
      case 'hashtag':
        return ['t', item.value]
      case 'url':
        return ['r', item.value]
      default:
        return ['e', item.value]
    }
  })
}

/**
 * Fetch user's list by type
 */
export async function fetchUserList(
  pubkey: string,
  listType: ListKind
): Promise<UserList | null> {
  console.log(`[ListService] fetchUserList started for ${pubkey}, type: ${listType}`)
  const pool = getNostrPool()
  const kind = getListKind(listType)
  const relays = getRelaysForList(listType)

  return new Promise((resolve) => {
    let latestEvent: Event | null = null

    const filter = {
      kinds: [kind],
      authors: [pubkey],
      limit: 1
    }

    // Add #d tag filter for parameterized lists
    if (kind >= 30000 && kind < 40000) {
      if (listType === 'moderation') {
        // @ts-ignore
        filter['#d'] = ['moderation_log']
      }
    }

    console.log(`[ListService] Subscribing with filter:`, JSON.stringify(filter))
    const sub = pool.subscribeMany(relays, filter as any, {
      onevent(event: Event) {
        console.log(`[ListService] fetchUserList event received:`, event.id, 'at', event.created_at)
        if (!latestEvent || event.created_at > latestEvent.created_at) {
          latestEvent = event
        }
      },
      oneose() {
        console.log(`[ListService] fetchUserList EOSE received. Latest event found:`, latestEvent?.id)
        if (latestEvent) {
          const nameTag = latestEvent.tags.find(t => t[0] === 'd')
          const descTag = latestEvent.tags.find(t => t[0] === 'description')

          resolve({
            id: latestEvent.id,
            name: nameTag?.[1] || listType,
            kind: latestEvent.kind,
            items: parseListItems(latestEvent.tags),
            createdAt: latestEvent.created_at,
            description: descTag?.[1]
          })
          return
        }
        // If EOSE happens and no latest event, wait for timeout still or resolve null?
        // Let's keep it consistent with the existing logic but add a log.
      }
    })

    setTimeout(() => {
      sub.close()
      console.log(`[ListService] fetchUserList timeout. Final result exists:`, !!latestEvent)
      if (latestEvent) {
        resolve({
          id: latestEvent.id,
          name: listType,
          kind: latestEvent.kind,
          items: parseListItems(latestEvent.tags),
          createdAt: latestEvent.created_at
        })
      } else {
        resolve(null)
      }
    }, 3000) // Reduced to 3s for better UX
  })
}

/**
 * Poll relays to verify the list was actually updated
 */
async function verifyListUpdate(
  pubkey: string,
  listType: ListKind,
  expectedItemValue: string,
  shouldExist: boolean
): Promise<boolean> {
  console.log(`[Verification] Polling for ${shouldExist ? 'addition' : 'removal'} of ${expectedItemValue}...`)

  // Poll for up to 10 seconds
  for (let i = 0; i < 5; i++) {
    console.log(`[Verification] Attempt ${i + 1}...`)
    const list = await fetchUserList(pubkey, listType)
    if (list) {
      console.log(`[Verification] Found list with ${list.items.length} items.`)
      const exists = list.items.some(k => k.value === expectedItemValue)
      console.log(`[Verification] Item exists in list: ${exists}`)
      if (exists === shouldExist) {
        console.log(`[Verification] MATCH FOUND!`)
        return true
      }
    } else {
      console.log(`[Verification] No list found yet for ${pubkey}`)
      if (!shouldExist) return true // If we want it gone and it's gone, that's success
    }
    await new Promise(r => setTimeout(r, 1500)) // Wait 1.5s
  }
  return false
}

export async function fetchBookmarks(pubkey: string): Promise<string[]> {
  const list = await fetchUserList(pubkey, 'bookmarks')
  if (!list) return []

  return list.items
    .filter(item => item.type === 'event')
    .map(item => item.value)
}

/**
 * Add item to list with Verification
 */
export async function addToList(
  listType: ListKind,
  item: ListItem,
  sk?: Uint8Array | null,
  listName?: string
): Promise<{ success: boolean; error?: string }> {
  console.log(`[ListService] addToList started: ${listType}, value: ${item.value}`)
  try {
    if (!sk && !window.nostr) {
      return { success: false, error: 'Giriş yapmalısınız' }
    }

    // Get current pubkey
    let pubkey: string
    if (sk) {
      const { getPublicKey } = await import('nostr-tools/pure')
      pubkey = getPublicKey(sk)
    } else {
      pubkey = await window.nostr!.getPublicKey()
    }
    console.log(`[ListService] Current pubkey:`, pubkey)

    // Fetch current list
    const currentList = await fetchUserList(pubkey, listType)
    const currentItems = currentList?.items || []
    console.log(`[ListService] Existing items count:`, currentItems.length)

    // Check if already exists
    const exists = currentItems.some(i => i.type === item.type && i.value === item.value)
    if (exists) {
      console.log(`[ListService] Item ALREADY EXISTS in list. Skip publish.`)
      return { success: true } // Already in list
    }

    // Add new item
    const newItems = [...currentItems, item]

    // Create updated list event
    const kind = getListKind(listType)
    const tags: string[][] = itemsToTags(newItems)

    // Add d-tag for parameterized replaceable events
    if (kind >= 30000 && kind < 40000) {
      tags.unshift(['d', listName || listType])
    }

    const eventTemplate = {
      kind,
      created_at: Math.floor(Date.now() / 1000),
      tags,
      content: ''
    }

    let signedEvent: Event

    if (sk) {
      signedEvent = finalizeEvent(eventTemplate, sk) as Event
    } else {
      signedEvent = await window.nostr!.signEvent(eventTemplate) as Event
    }

    // Publish to SPECIFIC relays for this list type
    const pool = getNostrPool()
    const relays = getRelaysForList(listType)

    console.log(`[ListService] Publishing to:`, relays)
    await Promise.allSettled(pool.publish(relays, signedEvent))

    // VERIFICATION STEP
    if (listType === 'moderation') {
      const verified = await verifyListUpdate(pubkey, listType, item.value, true)
      if (!verified) {
        return { success: false, error: 'Relay senkronizasyonu başarısız (Timeout). Lütfen tekrar deneyin.' }
      }
    }

    console.log(`📚 Added to ${listType}:`, item.value)
    return { success: true }

  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Listeye eklenemedi'
    }
  }
}

/**
 * Remove item from list with Verification
 */
export async function removeFromList(
  listType: ListKind,
  item: ListItem,
  sk?: Uint8Array | null,
  listName?: string
): Promise<{ success: boolean; error?: string }> {
  try {
    if (!sk && !window.nostr) {
      return { success: false, error: 'Giriş yapmalısınız' }
    }

    // Get current pubkey
    let pubkey: string
    if (sk) {
      const { getPublicKey } = await import('nostr-tools/pure')
      pubkey = getPublicKey(sk)
    } else {
      pubkey = await window.nostr!.getPublicKey()
    }

    // Fetch current list
    const currentList = await fetchUserList(pubkey, listType)
    if (!currentList) {
      return { success: true } // List doesn't exist
    }

    // Remove item
    const newItems = currentList.items.filter(
      i => !(i.type === item.type && i.value === item.value)
    )

    // Create updated list event
    const kind = getListKind(listType)
    const tags: string[][] = itemsToTags(newItems)

    // Add d-tag for parameterized replaceable events
    if (kind >= 30000 && kind < 40000) {
      tags.unshift(['d', listName || listType])
    }

    const eventTemplate = {
      kind,
      created_at: Math.floor(Date.now() / 1000),
      tags,
      content: ''
    }

    let signedEvent: Event

    if (sk) {
      signedEvent = finalizeEvent(eventTemplate, sk) as Event
    } else {
      signedEvent = await window.nostr!.signEvent(eventTemplate) as Event
    }

    // Publish
    const pool = getNostrPool()
    const relays = getRelaysForList(listType)
    await Promise.allSettled(pool.publish(relays, signedEvent))

    // VERIFICATION STEP
    if (listType === 'moderation') {
      const verified = await verifyListUpdate(pubkey, listType, item.value, false)
      if (!verified) {
        return { success: false, error: 'Relay senkronizasyonu başarısız (Timeout). Lütfen tekrar deneyin.' }
      }
    }

    console.log(`📚 Removed from ${listType}:`, item.value)
    return { success: true }

  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Listeden çıkarılamadı'
    }
  }
}

export async function toggleBookmark(
  eventId: string,
  sk?: Uint8Array | null
): Promise<{ success: boolean; isBookmarked: boolean; error?: string }> {
  try {
    if (!sk && !window.nostr) {
      return { success: false, isBookmarked: false, error: 'Giriş yapmalısınız' }
    }

    let pubkey: string
    if (sk) {
      const { getPublicKey } = await import('nostr-tools/pure')
      pubkey = getPublicKey(sk)
    } else {
      pubkey = await window.nostr!.getPublicKey()
    }

    const bookmarks = await fetchBookmarks(pubkey)
    const isCurrentlyBookmarked = bookmarks.includes(eventId)

    if (isCurrentlyBookmarked) {
      await removeFromList('bookmarks', { type: 'event', value: eventId }, sk)
      return { success: true, isBookmarked: false }
    } else {
      await addToList('bookmarks', { type: 'event', value: eventId }, sk)
      return { success: true, isBookmarked: true }
    }

  } catch (error) {
    return {
      success: false,
      isBookmarked: false,
      error: error instanceof Error ? error.message : 'Kaydetme başarısız'
    }
  }
}

export async function isBookmarked(
  eventId: string,
  pubkey: string
): Promise<boolean> {
  const bookmarks = await fetchBookmarks(pubkey)
  return bookmarks.includes(eventId)
}
